import { type ReactNode } from 'react'
import { Link } from '@muse-web/router'

import { IMAGE_RESIZE_SPEC } from '@core/image'
import { Avatar, AvatarFallback, AvatarImage, Cover } from '@ds/components'

import { COLLECTION_HERO_COVER_SIZES } from '../../constants/collectionHeroConstants'

import { CollectionDescription } from './CollectionDescription'

import styles from './CollectionHero.module.css'

export type TCollectionHeroProps = {
    coverSrc: string
    coverAlt: string
    title: string
    authorLabel: string
    authorAvatarSrc: string
    authorHref: string
    description: string
    /** Rendered instead of the raw description — the container decides whether links are allowed. */
    descriptionSlot?: ReactNode
    descriptionPlaceholder: string
    /** The same tag row twice: inside the description up to desktop, in its own row from desktop up. */
    tagsSlot: ReactNode
    followSlot?: ReactNode
    actionsSlot?: ReactNode
}

export const CollectionHero = ({
    coverSrc,
    coverAlt,
    title,
    authorLabel,
    authorAvatarSrc,
    authorHref,
    description,
    descriptionSlot,
    descriptionPlaceholder,
    tagsSlot,
    followSlot,
    actionsSlot,
}: TCollectionHeroProps) => (
    <section className={styles.hero}>
        <div className={styles.coverBox}>
            <Cover
                stacked
                src={coverSrc}
                alt={coverAlt}
                sizes={COLLECTION_HERO_COVER_SIZES}
                className={styles.cover}
                resize={IMAGE_RESIZE_SPEC}
            />
        </div>

        <div className={styles.body}>
            <div className={styles.titleGroup}>
                <h1 className={styles.title}>{title}</h1>

                <div className={styles.authorRow}>
                    <Link href={authorHref} className={styles.author}>
                        <Avatar className={styles.avatar}>
                            {authorAvatarSrc ? (
                                <AvatarImage
                                    src={authorAvatarSrc}
                                    alt={authorLabel}
                                    width={32}
                                    height={32}
                                    resize={IMAGE_RESIZE_SPEC}
                                />
                            ) : null}
                            <AvatarFallback />
                        </Avatar>
                        <span>{authorLabel}</span>
                    </Link>
                    {followSlot}
                </div>
            </div>

            <div>
                <CollectionDescription
                    description={description}
                    descriptionSlot={descriptionSlot}
                    placeholder={descriptionPlaceholder}
                    tagsSlot={tagsSlot}
                />
            </div>

            <div className={styles.footer}>
                <div className={styles.desktopTags}>{tagsSlot}</div>

                {actionsSlot ? <div className={styles.actions}>{actionsSlot}</div> : null}
            </div>
        </div>
    </section>
)
